import React, { useState, useEffect } from 'react';
import { getStatusChipSx, ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  AccountTreeOutlined as ChartIcon,
  ListOutlined as ListIcon,
  GroupsOutlined as CommitteeIcon,
  BusinessCenterOutlined as ExecutiveIcon,
  AccountBalanceOutlined as BoardIcon,
} from '@mui/icons-material';
import GovernanceStructureChart from '../../../components/charts/GovernanceStructureChart';
import { csAdminApi } from '../../../services/api';

const asArray = (value) => (Array.isArray(value) ? value : []);

const mapCommitteeForGovernance = (committee) => {
  const members = asArray(committee.members);
  const memberCount = committee.memberCount ?? committee.member_count ?? members.length;
  const chairperson = committee.chairperson || committee.chairperson_name;

  return {
    id: committee.id || committee.committee_id,
    name: committee.name || committee.committee_name,
    type: committee.type || committee.committee_type,
    status: committee.status || 'ACTIVE',
    memberCount,
    members: members.length
      ? members
      : chairperson
        ? [{ name: chairperson, role: 'CHAIRPERSON' }]
        : [],
    chairperson,
    meetingFrequency: committee.meetingFrequency,
    quorum: committee.quorum,
    quorumType: committee.quorumType,
    quorumPercentage: committee.quorumPercentage,
    quorumNumber: committee.quorumNumber,
  };
};

const mapOfficerForGovernance = (officer) => ({
  id: officer.id || officer.officer_id,
  name: officer.name || officer.officer_name,
  position: officer.position,
  email: officer.email,
  phone: officer.phone,
  appointmentDate: officer.appointmentDate || officer.appointment_date,
  isAuthorizedSignatory: Boolean(officer.isAuthorizedSignatory ?? officer.is_authorized_signatory),
  reportsTo: officer.reportsTo ?? officer.reports_to ?? null,
});

const GovernanceStructureTab = ({ companyId, company }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'summary'
  
  // Governance data
  const [boardData, setBoardData] = useState(null);
  const [committees, setCommittees] = useState([]);
  const [officers, setOfficers] = useState([]);

  useEffect(() => {
    fetchGovernanceData();
  }, [companyId]);

  const fetchGovernanceData = async () => {
    setLoading(true);
    try {
      const [directorsRes, committeesRes, officersRes] = await Promise.all([
          csAdminApi.getCompanyDirectors(companyId),
          csAdminApi.getCommittees({ company_id: companyId }),
          csAdminApi.getCompanyOfficers(companyId),
        ]);
        const directors = (directorsRes.data?.data || []).map((d) => ({
          id: d.id || d.director_id,
          name: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim(),
          position: d.position || d.role || 'Director',
          isIndependent: false,
          appointmentDate: d.appointment_date,
        }));
        const chair = directors.find((d) => /chair/i.test(d.position)) || directors[0];
        setBoardData({
          chairman: chair?.name || '—',
          directors,
        });
        setCommittees(asArray(committeesRes.data?.data).map(mapCommitteeForGovernance));
        setOfficers(asArray(officersRes.data?.data).map(mapOfficerForGovernance));
      setError('');
    } catch (err) {
      console.error('Error fetching governance data:', err);
      setError('Failed to load governance structure');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading governance structure...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Governance Structure
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Board of Directors, Committees & Executive Leadership
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Quick Stats */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<BoardIcon sx={{ fontSize: 16 }} />}
                label={`${boardData?.directors?.length || 0} Directors`}
                size="small"
                sx={{ bgcolor: '#E3F2FD', color: '#1976D2' }}
              />
              <Chip
                icon={<CommitteeIcon sx={{ fontSize: 16 }} />}
                label={`${committees.length} Committees`}
                size="small"
                sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2' }}
              />
              <Chip
                icon={<ExecutiveIcon sx={{ fontSize: 16 }} />}
                label={`${officers.length} Officers`}
                size="small"
                sx={{ bgcolor: '#FFF3E0', color: '#E65100' }}
              />
            </Box>

            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
            >
              <ToggleButton value="chart">
                <ChartIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Chart
              </ToggleButton>
              <ToggleButton value="summary">
                <ListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Summary
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* Chart View */}
      {viewMode === 'chart' && (
        <Paper sx={{ overflow: 'hidden' }}>
          <GovernanceStructureChart
            boardData={boardData}
            committees={committees}
            officers={officers}
            height={600}
          />
        </Paper>
      )}

      {/* Summary View */}
      {viewMode === 'summary' && (
        <Grid container spacing={2}>
          {/* Board of Directors */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BoardIcon sx={{ color: '#1976D2' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Board of Directors
                  </Typography>
                  <Chip 
                    label={`${boardData?.directors?.length || 0} Members`} 
                    size="small" 
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Grid container spacing={1}>
                  {boardData?.directors?.map((director) => (
                    <Grid item xs={12} sm={6} md={4} key={director.id}>
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: '#F5F5F5', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Box sx={{ 
                          width: 36, 
                          height: 36, 
                          borderRadius: '50%', 
                          bgcolor: '#E3F2FD',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1976D2',
                          fontWeight: 600,
                        }}>
                          {director.name?.charAt(0)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {director.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={director.position} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            {director.isIndependent && (
                              <Chip label="Independent" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: ACTIVE_GREEN_BG, color: ACTIVE_GREEN }} />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Committees */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CommitteeIcon sx={{ color: '#7B1FA2' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Committees
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {committees.map((committee) => (
                    <Box 
                      key={committee.id}
                      sx={{ 
                        p: 1.5, 
                        bgcolor: '#FAFAFA', 
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderColor: committee.type === 'Audit' ? '#1976D2' 
                          : committee.type === 'Risk' ? '#D32F2F'
                          : committee.type === 'Nomination' ? '#7B1FA2'
                          : committee.type === 'Investment' ? '#5D4037'
                          : '#757575',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {committee.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(Array.isArray(committee.members) ? committee.members.length : committee.memberCount || 0)} members
                            {committee.meetingFrequency ? ` • ${committee.meetingFrequency}` : ''}
                          </Typography>
                        </Box>
                        <Chip 
                          label={committee.status} 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.65rem',
                            ...getStatusChipSx(committee.status),
                          }} 
                        />
                      </Box>
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {asArray(committee.members).slice(0, 3).map((member, idx) => (
                          <Chip
                            key={idx}
                            label={member.name?.split(' ').slice(-1)[0]} // Last name only
                            size="small"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.65rem',
                              bgcolor: member.role === 'CHAIRPERSON' ? '#FFF3E0' : '#F5F5F5',
                              color: member.role === 'CHAIRPERSON' ? '#E65100' : '#757575',
                            }}
                          />
                        ))}
                        {(Array.isArray(committee.members) ? committee.members.length : committee.memberCount || 0) > 3 && (
                          <Chip
                            label={`+${(Array.isArray(committee.members) ? committee.members.length : committee.memberCount || 0) - 3}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Officers */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ExecutiveIcon sx={{ color: '#E65100' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Executive Officers
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {officers.map((officer) => {
                    const isCEO = officer.position?.toLowerCase().includes('ceo');
                    return (
                      <Box 
                        key={officer.id}
                        sx={{ 
                          p: 1.5, 
                          bgcolor: isCEO ? '#E3F2FD' : '#FAFAFA', 
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderColor: isCEO ? '#1565C0' 
                            : officer.position?.toLowerCase().includes('cfo') ? '#2E7D32'
                            : officer.position?.toLowerCase().includes('coo') ? '#E65100'
                            : '#757575',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {officer.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {officer.position}
                            </Typography>
                          </Box>
                          {officer.isAuthorizedSignatory && (
                            <Chip 
                              label="Signatory" 
                              size="small" 
                              sx={{ height: 18, fontSize: '0.65rem', bgcolor: ACTIVE_GREEN_BG, color: ACTIVE_GREEN }} 
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          {officer.email}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GovernanceStructureTab;
